import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { DreamSource } from '@wanderlust/shared';
import { captureDream } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { safeStorage } from '../lib/safeStorage';

/**
 * The call to action into `/plan` on the home page and the Turkey Trot page,
 * and the start of the Dreams funnel.
 *
 * A press writes one dream capture row with no dream in it and carries the
 * row's token to the form as `?dream=`. The form reports against that row from
 * there: that it opened, the dream as they write it into "Where are you
 * dreaming of going?", and anything that stopped them. So the report knows who
 * pressed and how far they got, and only shows a dream once one is written.
 *
 * A link rather than a `<button>`, so a crawler, a new tab and a page that has
 * not hydrated yet all still reach the form. Only a plain click is taken over to
 * carry the token; anything else is an ordinary visit to `/plan`.
 */

/** Kept in the browser, so a second press lands on the same row. */
const DREAM_TOKEN_KEY = 'wanderlust-dream-token';

/**
 * `crypto.randomUUID` is missing in non-secure contexts and before Safari 15.4.
 * The shape is all that matters here — the server only checks that it parses as
 * a uuid — and this sits behind the page's primary call to action, so it cannot
 * be the thing that throws.
 */
function mintToken(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the shim
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function dreamToken(): string {
  const stored = safeStorage.getItem(DREAM_TOKEN_KEY);
  if (stored) return stored;

  const token = mintToken();
  safeStorage.setItem(DREAM_TOKEN_KEY, token);
  return token;
}

function readUtm() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') ?? undefined,
    utm_medium: params.get('utm_medium') ?? undefined,
    utm_campaign: params.get('utm_campaign') ?? undefined,
  };
}

/**
 * `src` is capped at the 50 characters the API allows for `source`: an
 * over-long value fails the whole intake schema, and losing the lead to win the
 * attribution is the wrong trade.
 */
function planHref(leadSource?: string, token?: string | null): string {
  const params = new URLSearchParams();
  if (token) params.set('dream', token);
  if (leadSource) params.set('src', leadSource.slice(0, 50));
  const query = params.toString();
  return query ? `/plan?${query}` : '/plan';
}

interface StartPlanningButtonProps {
  /** Which button this is, reported on the row so each placement stays measurable. */
  source: DreamSource;
  /**
   * Stamped onto the Lead itself as `source`, via `?src=` on the intake link.
   * `source` above only reaches the dream row; a campaign landing page needs
   * the answer on the lead, which is where the follow-up actually happens.
   * Omitted on the home page, where the API's own default is right.
   */
  leadSource?: string;
}

export function StartPlanningButton({ source, leadSource }: StartPlanningButtonProps) {
  const navigate = useNavigate();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();

    // Recording the press is the secondary job. Getting them to the form is the
    // primary one, so nothing here can stop the navigation below.
    let token: string | null = null;
    try {
      token = dreamToken();
      captureDream({
        token,
        source,
        stage: 'SUBMITTED',
        referrer: document.referrer || undefined,
        ...readUtm(),
      }, true);
      trackEvent('dream-submitted', { placement: source });
    } catch {
      // capture is best-effort
    }

    navigate(planHref(leadSource, token));
  };

  return (
    <Link
      to={planHref(leadSource)}
      onClick={handleClick}
      className="inline-block px-12 py-4.5 bg-accent text-primary text-base font-medium uppercase tracking-wide rounded hover:bg-accent-hover hover:-translate-y-0.5 transition-all"
    >
      Start Planning
    </Link>
  );
}
